import { isPlaceType, isRetired, nameStem, type Discovery } from './discovery';
import { bbox, bearingDeg, bearingDiff, clamp, haversineM, type BBox } from './geo';
import { relativeBearing } from './intro';
import type { CleanFix, Landmark } from './types';

/** A landmark this far off the heading and this far away has been passed (the speech queue drops queued ones by the same rule). */
const PASSED_DEG = 110;
const PASSED_M = 1000;
/**
 * "Passed" is judged against the course over about this much road, not the heading of the moment:
 * on the Crown Point switchbacks the car headed 303° with Bridal Veil Falls 4.7 km off at 75°, the
 * falls counted as passed, and the Columbia's story started 83 s before they triggered.
 */
const COURSE_M = 2000;
const COURSE_STEP_M = 100;
/** Closer than this straight-line to where the course starts (a loop or hairpin), the course is unknown. */
const COURSE_MIN_M = 500;

export interface ProximityOptions {
  /** Exit radius = enter radius × this. Stops boundary flap. */
  hysteresis: number;
  /** Seconds of lead before the script should start, on top of the script's own length. */
  leadSec: number;
  /** Never trigger from farther than this (metres), however fast you drive. */
  maxTriggerM: number;
  /** A landmark is "ahead" if its bearing is within ± this of the heading (degrees). */
  approachConeDeg: number;
  /** Half-width of the local search box (km). Must exceed maxTriggerM × hysteresis. */
  searchKm: number;
  /**
   * How far the driver's attention reaches for an ordinary 1 km landmark, as [speed m/s, metres]
   * anchors, linear between them and flat beyond. A landmark's reach is this times its base
   * radius / 1000, so a mountain reaches four times as far as a shop.
   */
  reachCurve: [number, number][];
  /** A landmark within this of a story already told this trip is covered by it. */
  clusterM: number;
  /** ... and within this when the two share a name stem ("Orenco, Oregon" and "Orenco station"). */
  stemClusterM: number;
  /** A place counts as left once the car is this far past its edge (× radius), so GPS noise at the line is not an arrival. */
  placeExitFactor: number;
}

export const PROXIMITY_DEFAULTS: ProximityOptions = {
  hysteresis: 1.4,
  leadSec: 15,
  maxTriggerM: 4000,
  approachConeDeg: 75,
  searchKm: 8,
  // ≤9 mph 200 m, 20 mph 300, 30 mph 450, 40 mph 700, 50 mph 1100, 60 mph 1700, 70 mph 2400.
  reachCurve: [
    [4, 200],
    [9, 300],
    [13.4, 450],
    [17.9, 700],
    [22.4, 1100],
    [26.8, 1700],
    [31.3, 2400],
  ],
  clusterM: 400,
  stemClusterM: 1000,
  placeExitFactor: 1.1,
};

export interface ProximityDeps {
  /** Landmarks inside the box (the caller's spatial index; SQLite in the app, an array in tests). */
  landmarksNear(box: BBox): Promise<Landmark[]> | Landmark[];
  /** Has this landmark already been narrated on this trip? */
  isPlayed(pageId: number, tripId: string): Promise<boolean> | boolean;
  /** The lasting record across trips (discoveries table). Without it nothing is ever retired. */
  heard?(pageId: number): Pick<Discovery, 'last_at'> | null | Promise<Pick<Discovery, 'last_at'> | null>;
  /** A landmark should be narrated now. */
  onTrigger(lm: Landmark, distM: number, triggerM: number, fix: CleanFix): void | Promise<void>;
  /**
   * A landmark stayed quiet because a neighbour's story, already spoken this trip, covered it.
   * Once per landmark per trip; the app records it as heard so the cluster shares one cooldown.
   */
  onCovered?(lm: Landmark, byPageId: number, fix: CleanFix): void | Promise<void>;
  /** We have driven away from a landmark we were inside. */
  onExit(pageId: number): void | Promise<void>;
  /**
   * Fresh distance to a landmark we are inside, every fix, and its bearing relative to the heading
   * (null without one). Feed both to SpeechQueue.updateDistance.
   */
  onDistance?(pageId: number, distM: number, relDeg: number | null): void;
}

export const isPlace = (lm: Pick<Landmark, 'wiki_type'>): boolean => isPlaceType(lm.wiki_type);

/** Piecewise-linear lookup, flat beyond the first and last anchors. */
function lookup(curve: readonly [number, number][], x: number): number {
  const first = curve[0];
  if (!first) return 0;
  if (x <= first[0]) return first[1];
  for (let i = 1; i < curve.length; i++) {
    const [x1, y1] = curve[i]!;
    if (x <= x1) {
      const [x0, y0] = curve[i - 1]!;
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }
  return curve[curve.length - 1]![1];
}

const scaleOf = (lm: Landmark): number => Math.max(1, lm.trigger_radius_m / 1000);

/**
 * Inside this distance a landmark counts as "here" whatever the heading: 200 m for an ordinary
 * one, scaled up for big ones. A town is here anywhere inside its radius.
 */
export function nearRadiusM(lm: Landmark, opts: ProximityOptions = PROXIMITY_DEFAULTS): number {
  if (isPlace(lm)) return lm.trigger_radius_m;
  return (opts.reachCurve[0]?.[1] ?? 200) * scaleOf(lm);
}

/**
 * How far out a landmark triggers at this speed.
 *
 * What it wants is the base radius, or the timing distance when that is farther (script length
 * plus lead, so it ends as you arrive). Attention reach (reachCurve, scaled by the base radius)
 * caps it: crawling through town only what is close by speaks, and on the highway the reach opens
 * past both, so highway timing is what it always was. The base radius used to be a floor, so at
 * 30 mph a shop still triggered a kilometre out and at 45 mph the Orenco trio fired from 1.4 km
 * (first drive with the V40 in Hillsboro, 2026-09-22). A mountain's reach is four times a shop's,
 * so it still triggers from its full 4 km at 50 mph.
 *
 * Places (towns) trigger at their radius, which is the town's edge: arrival does not depend on speed.
 */
export function triggerDistanceM(lm: Landmark, speedMps: number | null, opts: ProximityOptions = PROXIMITY_DEFAULTS): number {
  const base = lm.trigger_radius_m;
  if (isPlace(lm)) return base;
  const v = speedMps ?? 0;
  const reach = lookup(opts.reachCurve, v) * scaleOf(lm);
  const wanted = Math.max(base, v * (lm.est_seconds + opts.leadSec));
  return clamp(Math.min(reach, wanted), nearRadiusM(lm, opts), Math.max(base, opts.maxTriggerM));
}

/** A story told this trip, remembered so its neighbours can be covered by it. */
interface Told {
  id: number;
  lat: number;
  lon: number;
  stem: string | null;
}

export interface ProximityState {
  inside: [number, number][];
  /** Where this trip started. A place the trip began inside has not been arrived at. */
  start?: { lat: number; lon: number } | null;
  /** Places the car has been clearly outside of during this trip. */
  outside?: number[];
  told?: Told[];
  covered?: number[];
}

/**
 * Decides when a landmark should be narrated. Stateful (which landmarks we are "inside", where
 * the trip started, what was told), so persist exportState() in the app if the process may die.
 * importState(null) starts a new trip.
 */
export class ProximityEngine {
  readonly opts: ProximityOptions;
  /** page_id -> the enter radius that fired, so the exit radius matches. */
  readonly inside = new Map<number, number>();
  private lastDist = new Map<number, number>();
  private start: { lat: number; lon: number } | null = null;
  private outside = new Set<number>();
  private told: Told[] = [];
  private covered = new Set<number>();
  /** Positions every 100 m over the last 2 km of road, for the course (not persisted). */
  private trail: { lat: number; lon: number }[] = [];

  constructor(private deps: ProximityDeps, opts: Partial<ProximityOptions> = {}) {
    this.opts = { ...PROXIMITY_DEFAULTS, ...opts };
  }

  async evaluate(fix: CleanFix, tripId: string): Promise<void> {
    const { latitude: lat, longitude: lon } = fix.coords;
    this.start ??= { lat, lon };
    const last = this.trail[this.trail.length - 1];
    if (!last || haversineM(last.lat, last.lon, lat, lon) >= COURSE_STEP_M) {
      this.trail.push({ lat, lon });
      if (this.trail.length > COURSE_M / COURSE_STEP_M + 1) this.trail.shift();
    }
    const near = await this.deps.landmarksNear(bbox(lat, lon, this.opts.searchKm));
    const seen = new Set<number>();

    for (const lm of near) {
      seen.add(lm.page_id);
      const dist = haversineM(lat, lon, lm.lat, lm.lon);
      const prevDist = this.lastDist.get(lm.page_id);
      this.lastDist.set(lm.page_id, dist);
      const place = isPlace(lm);
      if (place && dist > lm.trigger_radius_m * this.opts.placeExitFactor) this.outside.add(lm.page_id);

      const enterR = this.inside.get(lm.page_id);
      if (enterR == null) {
        const trig = triggerDistanceM(lm, fix.speedMps, this.opts);
        if (dist > trig) continue;
        // Beyond the near radius we only fire when actually heading toward it; inside it we are
        // there regardless of direction.
        const nearR = nearRadiusM(lm, this.opts);
        if (dist > nearR && !this.isApproaching(fix, lm, dist, prevDist)) continue;
        if (place && !this.arrived(lm)) continue;
        if (!place) {
          const mate = this.mateOf(lm);
          if (mate) {
            await this.maybeCovered(lm, mate, fix, tripId);
            continue;
          }
        }

        this.inside.set(lm.page_id, Math.max(trig, nearR));
        if (await this.deps.isPlayed(lm.page_id, tripId)) continue;
        if (await this.retired(lm, fix)) continue;
        this.told.push({ id: lm.page_id, lat: lm.lat, lon: lm.lon, stem: nameStem(lm.title) });
        await this.deps.onTrigger(lm, dist, trig, fix);
      } else if (dist > enterR * this.opts.hysteresis) {
        this.inside.delete(lm.page_id);
        await this.deps.onExit(lm.page_id);
      } else {
        this.deps.onDistance?.(lm.page_id, dist, relativeBearing(lm, fix));
      }
    }

    // Anything we were inside that has left the search box entirely.
    for (const id of [...this.inside.keys()]) {
      if (seen.has(id)) continue;
      this.inside.delete(id);
      this.lastDist.delete(id);
      await this.deps.onExit(id);
    }
  }

  /**
   * A triggered story never spoke (the queue dropped it), so it no longer covers its neighbours:
   * one of them may still speak for the cluster.
   */
  release(pageId: number): void {
    this.told = this.told.filter((t) => t.id !== pageId);
  }

  /**
   * Would an unplayed landmark trigger within `withinSec` at the current speed? Filler asks before
   * starting a ninety-second rundown, so it never sits on top of a landmark's moment. Direction is
   * deliberately ignored: with 200 m of GPS noise the heading swung Bridal Veil Falls out of the
   * approach cone, the Columbia rundown started, and the falls waited ninety seconds behind it.
   * Erring the other way only delays a rundown until a passed landmark is out of reach.
   *
   * `skipPassed` leaves out landmarks the car has clearly passed (the speech queue's own rule:
   * more than 110° off the heading and over 1 km away). Features ask with it: they play in the
   * first quiet gap rather than after ten quiet minutes, and in the Gorge (66 landmarks in the
   * corridor) some untold landmark behind the car was always within reach, so the Columbia never
   * played on the emulator. A landmark ahead or beside still holds a feature back.
   */
  async upcoming(fix: CleanFix, tripId: string, withinSec: number, skipPassed = false): Promise<boolean> {
    const { latitude: lat, longitude: lon } = fix.coords;
    const reach = (fix.speedMps ?? 0) * withinSec;
    // On a loop (Crown Point) the course is unknown and nothing counts as passed: waiting is safe.
    const course = skipPassed ? this.course(lat, lon) : null;
    for (const lm of await this.deps.landmarksNear(bbox(lat, lon, this.opts.searchKm))) {
      if (this.inside.has(lm.page_id)) continue;
      const dist = haversineM(lat, lon, lm.lat, lm.lon);
      if (dist - triggerDistanceM(lm, fix.speedMps, this.opts) > reach) continue;
      if (course != null && dist > PASSED_M && Math.abs(bearingDiff(course, bearingDeg(lat, lon, lm.lat, lm.lon))) > PASSED_DEG) continue;
      const place = isPlace(lm);
      if (place ? !this.arrived(lm) : this.mateOf(lm)) continue;
      if (await this.deps.isPlayed(lm.page_id, tripId)) continue;
      if (await this.retired(lm, fix)) continue;
      return true;
    }
    return false;
  }

  /**
   * Starting a trip inside a town is not arriving there: home sits 3.1 km inside Hillsboro's
   * 4.6 km radius, and the town played on the first fix of every trip. It counts once the car has
   * been clearly outside it this trip.
   */
  private arrived(lm: Landmark): boolean {
    if (this.outside.has(lm.page_id)) return true;
    return !this.start || haversineM(this.start.lat, this.start.lon, lm.lat, lm.lon) > lm.trigger_radius_m;
  }

  /** A story told this trip that this landmark sits beside, if any. */
  private mateOf(lm: Landmark): Told | null {
    const stem = nameStem(lm.title);
    for (const t of this.told) {
      if (t.id === lm.page_id) continue;
      const d = haversineM(t.lat, t.lon, lm.lat, lm.lon);
      if (d <= this.opts.clusterM || (stem != null && stem === t.stem && d <= this.opts.stemClusterM)) return t;
    }
    return null;
  }

  /** Report a covered landmark once its neighbour has actually spoken (a queued one might still be dropped). */
  private async maybeCovered(lm: Landmark, mate: Told, fix: CleanFix, tripId: string): Promise<void> {
    if (this.covered.has(lm.page_id) || !this.deps.onCovered) return;
    if (!(await this.deps.isPlayed(mate.id, tripId))) return;
    this.covered.add(lm.page_id);
    if (await this.deps.isPlayed(lm.page_id, tripId)) return;
    await this.deps.onCovered(lm, mate.id, fix);
  }

  private async retired(lm: Landmark, fix: CleanFix): Promise<boolean> {
    if (!this.deps.heard) return false;
    return isRetired(await this.deps.heard(lm.page_id), isPlace(lm), fix.timestamp);
  }

  private isApproaching(fix: CleanFix, lm: Landmark, dist: number, prevDist: number | undefined): boolean {
    if (fix.heading != null) {
      const b = bearingDeg(fix.coords.latitude, fix.coords.longitude, lm.lat, lm.lon);
      return Math.abs(bearingDiff(fix.heading, b)) <= this.opts.approachConeDeg;
    }
    if (prevDist != null) return dist < prevDist;
    return false;
  }

  /** The bearing from where the car was about 2 km of road ago, or null on a short trail or a hairpin. */
  private course(lat: number, lon: number): number | null {
    const from = this.trail[0];
    if (!from || haversineM(from.lat, from.lon, lat, lon) < COURSE_MIN_M) return null;
    return bearingDeg(from.lat, from.lon, lat, lon);
  }

  exportState(): ProximityState {
    return {
      inside: [...this.inside.entries()],
      start: this.start,
      outside: [...this.outside],
      told: this.told,
      covered: [...this.covered],
    };
  }

  /** Restore after a process restart, or pass null to start a new trip. */
  importState(s: ProximityState | null | undefined): void {
    this.inside.clear();
    for (const [id, r] of s?.inside ?? []) this.inside.set(id, r);
    this.lastDist.clear();
    this.trail = [];
    this.start = s?.start ?? null;
    this.outside = new Set(s?.outside ?? []);
    this.told = [...(s?.told ?? [])];
    this.covered = new Set(s?.covered ?? []);
  }
}
